/**
 * progressStream.js — client side of the backend's NDJSON progress channel
 * (see src/lib/analisis_penerbitan/progressStream.js in the API).
 *
 * Endpoints called with `?stream=1` reply with newline-delimited JSON that grows
 * while the server works:
 *
 *   {"t":"p","pct":62,"done":75500,"total":118409,"label":"…","unit":"baris"}
 *   {"t":"p","pct":0,"holdTo":30,"holdMs":3003,…}      <- dead-air hint
 *   {"t":"end","status":201,"body":{…}}                <- always last
 *
 * XHR exposes the partial body on every download-progress tick, so the reader
 * walks whole lines as they land and hands back the newest progress record.
 *
 * Because headers are sent before the work finishes, the HTTP status is always
 * 200 and the real one is inside the final line — callers must read `end.status`
 * rather than trusting the response code.
 */

/** Incremental reader over one growing response body. One per request. */
export class ProgressReader {
  constructor() {
    this.pos = 0; // chars already consumed
  }

  /**
   * Feed the current (partial) response text.
   * @returns the newest {t:'p'} record in the newly arrived lines, or null.
   */
  read(text) {
    if (typeof text !== "string" || !text) return null;
    const nl = text.lastIndexOf("\n");
    if (nl < 0 || nl < this.pos) return null;
    const lines = text.slice(this.pos, nl).split("\n");
    this.pos = nl + 1;
    // Older lines in the same tick are already stale; the newest one wins.
    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i].trim();
      if (!line) continue;
      let msg = null;
      try { msg = JSON.parse(line); } catch (err) { continue; }
      if (msg && msg.t === "p") return msg;
    }
    return null;
  }
}

/**
 * Pull the terminating {t:'end'} record out of a completed body.
 * Returns null when it isn't there, which means the connection died before the
 * server finished — the caller must treat that as a failure, not as success.
 */
export function readEnd(body) {
  const lines = String(body || "").split("\n");
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (!line) continue;
    let msg = null;
    try { msg = JSON.parse(line); } catch (err) { continue; }
    if (msg && msg.t === "end") return msg;
  }
  return null;
}

/** Fresh progress state for a single operation. */
export function newProgress() {
  return { active: false, pct: 0, label: "", done: 0, total: 0, unit: "", eta: null };
}

/**
 * Apply one progress record to a state object.
 *
 * The percentage only ever moves forward: a step that turns out to be empty
 * jumps ahead rather than rewinding. `holdMs` records are dead-air hints and
 * carry no real reading, so they update the wording only — the caller animates
 * the bar for those.
 */
export function applyProgress(state, msg, opts) {
  if (!msg) return state;
  state.active = true;
  state.label = msg.label || "";
  state.done = msg.done || 0;
  state.total = msg.total || 0;
  state.unit = msg.unit || "";
  if (!msg.holdMs) {
    const v = Math.min(100, Math.max(0, Math.round(Number(msg.pct) || 0)));
    if (v > state.pct) state.pct = v;
    state.eta = estimateEta(state, opts && opts.startedAt);
  }
  return state;
}

/**
 * Seconds remaining, from how long the bar has taken to get where it is.
 *
 * Deliberately based on the overall percentage rather than the current step's
 * row count: the steps have very different per-unit costs, so extrapolating
 * from "rows done in this step" would swing wildly at every step boundary.
 * Returns null until there is enough signal to be worth showing.
 */
export function estimateEta(state, startedAt) {
  if (!startedAt || state.pct < 5 || state.pct >= 100) return null;
  const elapsed = (Date.now() - startedAt) / 1000;
  if (elapsed < 1) return null;
  return Math.round((elapsed / state.pct) * (100 - state.pct));
}

/** "3 saat" / "2 minit" / "1 jam 5 minit" */
export function fmtEta(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s} saat`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minit`;
  return `${Math.floor(m / 60)} jam ${m % 60} minit`;
}
